import {cpServiceV2Post} from 'api';
import {sendSentryException} from 'utils/error-handling';
import {asDecimalCurrency, asInteger, formatPrice} from 'utils/number-format';
import {
    type GetYoyBreakdownRawResponse,
    type GetYoyDashboardSummaryRawResponse,
    type YoyGraphData,
    type YoyGraphDataRawResponse,
    type YoyRequest,
} from 'api/accounting/accounting.types.js';
import {type AxiosRequestConfig} from 'axios';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';

/**
 * Formats the request data for the dashboard yoy summary api endpoints
 * @param data - The data to format
 *
 * @group DTO Formatters
 *
 */
export const formatGetYoySummary = (
    data: GetYoyDashboardSummaryRawResponse
) => {
    const formattedData = camelizeKeys(data);

    const formattedRevenue = {
        ...formattedData.revenue,
        value: asDecimalCurrency(formattedData.revenue.value / 100),
        previousValue: asDecimalCurrency(
            formattedData.revenue.previousValue / 100
        ),
    };

    const formattedReservations = {
        ...formattedData.reservations,
        value: asInteger(formattedData.reservations.value),
        previousValue: asInteger(formattedData.reservations.previousValue),
    };

    return {
        revenue: formattedRevenue,
        reservations: formattedReservations,
    };
};
/**
 * Formats the request data for the dashboard yoy summary api endpoints
 * @param data - The data to format
 *
 * @group DTO Formatters
 */
export const formatRequestData = (data: YoyRequest) => {
    const formattedData = {
        facilityIds: data.facilityIds.map(Number),
        currentDate: data.currentDate,
        year: Number(data.currentYear),
    };

    const formattedRequest = convertKeysToSnakeCase(formattedData);

    return formattedRequest;
};
/**
 * Gets the dashboard yoy accounting data for a collection of facilities
 * @param data - The parameters for the report
 * @param config - The axios config
 *
 * @see '/control-panel-service/v2/yoy/tiles'
 */
export const getYoySummary = async (
    data: YoyRequest,
    config: AxiosRequestConfig = {}
) => {
    try {
        const formattedRequest = formatRequestData(data);

        const response =
            await cpServiceV2Post<GetYoyDashboardSummaryRawResponse>(
                'yoy/tiles',
                formattedRequest,
                config
            );

        const formattedResponse = formatGetYoySummary(response.data.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats the request data for the dashboard yoy graphs api endpoints
 * @param data - The data to format
 *
 * @group DTO Formatters
 */
export const formatGetYoyGraphs = (data: YoyGraphDataRawResponse) => {
    const formattedYoyData = camelizeKeys(data);

    const formattedData = (innerData: YoyGraphData[], isRevenue: boolean) => {
        return innerData.map((item) => {
            const id: string = item.month;
            const values = Object.entries(item) // Convert object to array of key-value pairs
                .filter(([key]: [string, number]) => key !== 'month') // Exclude the 'month' key
                .map(([key, value]: [string, number]) => ({
                    [key]: isRevenue
                        ? formatPrice(value, {formatterCallback: Number})
                        : value,
                })) // Convert cents to dollars
                .reduce((acc, curr) => ({...acc, ...curr}), {}); // Merge all objects into a single object

            return {id, ...values};
        });
    };

    const formattedResponse = {
        revenue: formattedData(formattedYoyData.revenue, true),
        reservations: formattedData(formattedYoyData.reservations, false),
    };

    return formattedResponse;
};

/**
 * Gets the dashboard yoy accounting data for a collection of facilities
 * @param  data - The parameters for the report
 * @param  config - The axios config
 *
 * @see '/control-panel-service/v2/yoy/graphs
 */
export const getYoyGraphs = async (
    data: YoyRequest,
    config: AxiosRequestConfig = {}
) => {
    try {
        const formattedRequest = formatRequestData(data);

        const response = await cpServiceV2Post<YoyGraphDataRawResponse>(
            'yoy/graphs',
            formattedRequest,
            config
        );

        const formattedResponse = formatGetYoyGraphs(response.data.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats the request data for the dashboard yoy breakdown api endpoints
 * @param  data - The data to format
 *
 * @group DTO Formatters
 */
export const formatGetYoyBreakdown = (
    data: GetYoyBreakdownRawResponse['data']
) => {
    const formattedData = camelizeKeys(data);

    const updatedDated = formattedData.map((item) => ({
        ...item,
        currentRating: item.currentRating,
        revenue: {
            ...item.revenue,
            value: asDecimalCurrency(item.revenue.value / 100),
            previousValue: asDecimalCurrency(item.revenue.previousValue / 100),
            previousTotal: asDecimalCurrency(item.revenue.previousTotal / 100),
        },
        reservations: {
            ...item.reservations,
            value: asInteger(item.reservations.value),
            previousValue: asInteger(item.reservations.previousValue),
            previousTotal: asInteger(item.reservations.previousTotal),
        },
    }));

    return updatedDated;
};

/**
 * Gets an YOY breakdown for a given set of facilities
 * @param data - The parameters for the report
 * @param config - The axios config
 * @throws {Error}
 *
 * @see '/control-panel-service/v2/yoy/facility-breakdown
 */
export const getYoyBreakdown = async (
    data: YoyRequest,
    config: AxiosRequestConfig = {}
) => {
    try {
        const formattedRequest = formatRequestData(data);

        const response = await cpServiceV2Post<
            GetYoyBreakdownRawResponse['data']
        >('yoy/facility-breakdown', formattedRequest, config);

        const formattedResponse = formatGetYoyBreakdown(
            response.data.data.results
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
